import { AgelessAssessmentFlowType, AgelessAssessmentStep } from "@orval/model";

import type { CombinedFlowOrder } from "../useFlowOrder/useFlowOrder";

export type AssessmentStepMeta = {
  id: AgelessAssessmentStep;
  slug: string;
  label: string;
  requiresAuth: boolean;
  optional?: true;
  fullScreen?: boolean;
  requiresVerification?: boolean;
  aliases?: string[];
};

const PRESCAN_STEP: AssessmentStepMeta = {
  id: AgelessAssessmentStep.PreScan,
  slug: "pre-scan",
  label: "PreScan",
  requiresAuth: false,
  requiresVerification: false,
};

const STUDIO_STEP: AssessmentStepMeta = {
  id: AgelessAssessmentStep.ImageCapture,
  slug: "studio",
  label: "Studio",
  requiresAuth: true,
  requiresVerification: false,
  fullScreen: true,
};

const VERIFY_INFO_STEP: AssessmentStepMeta = {
  id: AgelessAssessmentStep.VerifyInfo,
  slug: "reveal",
  label: "Reveal",
  requiresAuth: true,
  requiresVerification: false,
};

const SCORE_STEP: AssessmentStepMeta = {
  id: AgelessAssessmentStep.Score,
  slug: "score",
  label: "Score",
  requiresAuth: true,
  requiresVerification: true,
  fullScreen: true,
};

const TRANSFORMATION_STEP: AssessmentStepMeta = {
  id: AgelessAssessmentStep.Transformation,
  slug: "transformation",
  label: "Transformation",
  requiresAuth: true,
  requiresVerification: true,
  fullScreen: true,
};

const NEXT_STEPS_STEP: AssessmentStepMeta = {
  id: AgelessAssessmentStep.NextSteps,
  slug: "next-steps",
  label: "Next Steps",
  requiresAuth: true,
  requiresVerification: true,
};

const FUTURE_SELF_STEP: AssessmentStepMeta = {
  id: AgelessAssessmentStep.FutureSelf,
  slug: "future",
  label: "Future Self",
  requiresAuth: true,
  requiresVerification: true,
  fullScreen: true,
  aliases: ["future-self"],
};

const RESULTS_STEP: AssessmentStepMeta = {
  id: AgelessAssessmentStep.Results,
  slug: "results",
  label: "Results",
  requiresAuth: true,
  requiresVerification: true,
};

const WELLNESS_PRESCAN_STEP: AssessmentStepMeta = {
  id: AgelessAssessmentStep.WellnessPreScan,
  slug: "pre-scan",
  label: "Wellness PreScan",
  requiresAuth: false,
  requiresVerification: false,
};

const WELLNESS_VERIFY_INFO_STEP: AssessmentStepMeta = {
  id: AgelessAssessmentStep.WellnessVerifyInfo,
  slug: "wellness-reveal",
  label: "Wellness Reveal",
  requiresAuth: true,
  requiresVerification: false,
};

const WELLNESS_IMAGE_CAPTURE_STEP: AssessmentStepMeta = {
  id: AgelessAssessmentStep.WellnessImageCapture,
  slug: "body",
  label: "Wellness Image capture",
  requiresAuth: true,
  requiresVerification: false,
  aliases: ["upload"],
};

const WELLNESS_TRANSFORMATION_STEP: AssessmentStepMeta = {
  id: AgelessAssessmentStep.WellnessTransformation,
  slug: "wellness-transformation",
  label: "Wellness Transformation",
  requiresAuth: true,
  requiresVerification: true,
  fullScreen: true,
};

const WELLNESS_RESULTS_STEP: AssessmentStepMeta = {
  id: AgelessAssessmentStep.WellnessResults,
  slug: "results",
  label: "Wellness Results",
  requiresAuth: true,
  requiresVerification: true,
};

const AESTHETICS_STEPS: AssessmentStepMeta[] = [
  PRESCAN_STEP,
  STUDIO_STEP,
  VERIFY_INFO_STEP,
  SCORE_STEP,
  TRANSFORMATION_STEP,
  FUTURE_SELF_STEP,
  RESULTS_STEP,
];

const WELLNESS_STEPS: AssessmentStepMeta[] = [
  WELLNESS_PRESCAN_STEP,
  WELLNESS_IMAGE_CAPTURE_STEP,
  WELLNESS_VERIFY_INFO_STEP,
  WELLNESS_TRANSFORMATION_STEP,
  WELLNESS_RESULTS_STEP,
];

// Lash & Brow reuses the generic `AgelessAssessmentStep` values (the
// backend enum has no `LashBrow*` variants) and the `LashBrowConsumerFlowSaga`
// orchestrates around the same generic step IDs.
const LASH_BROW_STEPS: AssessmentStepMeta[] = [
  {
    id: AgelessAssessmentStep.PreScan,
    slug: "goals",
    label: "Goals",
    requiresAuth: false,
    requiresVerification: false,
    aliases: ["pre-scan"],
  },
  {
    id: AgelessAssessmentStep.ImageCapture,
    slug: "image-capture",
    label: "Image",
    requiresAuth: true,
    requiresVerification: false,
    fullScreen: true,
    aliases: ["studio"],
  },
  {
    id: AgelessAssessmentStep.VerifyInfo,
    slug: "verify",
    label: "Verify",
    requiresAuth: true,
    requiresVerification: false,
    aliases: ["reveal"],
  },
  {
    id: AgelessAssessmentStep.Score,
    slug: "analysis",
    label: "Analysis",
    requiresAuth: true,
    requiresVerification: true,
    fullScreen: true,
    aliases: ["score"],
  },
  {
    id: AgelessAssessmentStep.Transformation,
    slug: "playground",
    label: "Playground",
    requiresAuth: true,
    requiresVerification: true,
    fullScreen: true,
    aliases: ["transformation"],
  },
  {
    id: AgelessAssessmentStep.Results,
    slug: "results",
    label: "Results",
    requiresAuth: true,
    requiresVerification: true,
  },
];

const COMBINED_STEPS_AESTHETICS_FIRST: AssessmentStepMeta[] = [
  PRESCAN_STEP,
  STUDIO_STEP,
  VERIFY_INFO_STEP,
  SCORE_STEP,
  TRANSFORMATION_STEP,
  NEXT_STEPS_STEP,
  FUTURE_SELF_STEP,
  WELLNESS_IMAGE_CAPTURE_STEP,
  WELLNESS_VERIFY_INFO_STEP,
  WELLNESS_TRANSFORMATION_STEP,
  RESULTS_STEP,
];

const COMBINED_STEPS_WELLNESS_FIRST: AssessmentStepMeta[] = [
  PRESCAN_STEP,
  WELLNESS_IMAGE_CAPTURE_STEP,
  WELLNESS_VERIFY_INFO_STEP,
  WELLNESS_TRANSFORMATION_STEP,
  STUDIO_STEP,
  VERIFY_INFO_STEP,
  SCORE_STEP,
  TRANSFORMATION_STEP,
  FUTURE_SELF_STEP,
  RESULTS_STEP,
];

export const getStepMetaForFlow = (
  flowType: AgelessAssessmentFlowType,
  flowOrder: CombinedFlowOrder | null = null,
): AssessmentStepMeta[] => {
  switch (flowType) {
    case AgelessAssessmentFlowType.Aesthetics:
      return AESTHETICS_STEPS;
    case AgelessAssessmentFlowType.Wellness:
      return WELLNESS_STEPS;
    case AgelessAssessmentFlowType.LashBrow:
      return LASH_BROW_STEPS;
    case AgelessAssessmentFlowType.AestheticsWellness:
      return flowOrder === "BodyFirst"
        ? COMBINED_STEPS_WELLNESS_FIRST
        : COMBINED_STEPS_AESTHETICS_FIRST;
    default:
      throw new Error(`Unknown flow type: ${flowType}`);
  }
};

/**
 * Resolve a step by its canonical slug or any registered alias.
 * Returns `undefined` when neither matches.
 */
export const findStepBySlugOrAlias = (
  steps: AssessmentStepMeta[],
  slug: string,
): AssessmentStepMeta | undefined => {
  return steps.find((s) => s.slug === slug) ?? steps.find((s) => s.aliases?.includes(slug));
};
